import type { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import { ErrorADT, safe } from "pb.safe";

import { $, query } from "../../../../../schema.dev-sdk-api-appsync.generated";
import type { ApiClient } from "../../shared/shared/api-client";

export async function getVirtualTerminalContext({
	apiClient,
	...variables
}: {
	apiClient: ApiClient;
} & VariablesOf<
	typeof getVirtualTerminalContext.gql.getDeviceSettings
>): Promise<ErrorADT<"Network" | "Setup"> | VirtualTerminalContext> {
	const result = await safe(() =>
		apiClient.request(
			getVirtualTerminalContext.gql.getDeviceSettings,
			variables,
		),
	);

	if (result instanceof Error) {
		return ErrorADT("Network");
	}

	const siteId = result.getDeviceSettings.site?.siteUuid;
	if (!siteId) {
		return ErrorADT("Setup");
	}

	return {
		entityId: result.getDeviceSettings.entityUuid,
		deviceId: variables.deviceId,
		siteId,
		// Default to "AUD" if no currency found.
		currency: result.getDeviceSettings.entity?.currency ?? "AUD",
		// If no surcharge information, then surcharges will be $0.00.
		surchargesTaxes: result.getDeviceSettings.surchargesTaxes,
	};
}

getVirtualTerminalContext.gql = {
	getDeviceSettings: query("getDeviceSettings", (q) => [
		q.getDeviceSettings(
			{
				deviceUuid: $("deviceId"),
			},
			(q) => [
				q.entityUuid,
				q.entity((q) => [q.currency]),
				q.site((q) => [q.siteUuid]),
				q.surchargesTaxes((q) => [
					q.feesSurchargePbl((q) => [
						q.surchargeEnabled,
						q.surchargeFullFees,
						q.surchargePercent,
						q.feeFixed,
						q.feePercent,
					]),
				]),
			],
		),
	]),
};

export type VirtualTerminalContext = {
	deviceId: string;
	entityId: string;
	siteId: string;
	currency: string;
	surchargesTaxes: ResultOf<
		typeof getVirtualTerminalContext.gql.getDeviceSettings
	>["getDeviceSettings"]["surchargesTaxes"];
};
